/**
 * components/input.tsx — the design's `.t-input` text field (+ the builder's
 * `.bld-tok-num` numeric token). Focus = accent border + focus ring.
 */
import React, { useState } from 'react';
import { View, TextInput, StyleProp, ViewStyle, Platform } from 'react-native';
import { LightColors as C, Radii as R, FontSize as FS, FontFamily as FF, FontWeight as FW } from '../theme/theme';
import Icon, { IconName } from './icon';

export interface TextFieldProps {
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  sm?: boolean;
  numeric?: boolean;
  mono?: boolean;
  leftIcon?: IconName;
  onSubmit?: () => void;
  style?: StyleProp<ViewStyle>;
}

const focusRing = Platform.OS === 'web' ? ({ boxShadow: `0 0 0 3px ${C.accentSoft}` } as object) : {};

const TextField: React.FC<TextFieldProps> = ({
  value,
  onChangeText,
  placeholder,
  sm = false,
  numeric = false,
  mono = false,
  leftIcon,
  onSubmit,
  style,
}) => {
  const [focused, setFocused] = useState(false);
  const h = sm ? 28 : 34;

  return (
    <View
      style={[
        {
          height: h,
          flexDirection: 'row',
          alignItems: 'center',
          borderWidth: 1,
          borderColor: focused ? C.accent : C.line,
          backgroundColor: C.card,
          borderRadius: R.r2,
          paddingHorizontal: sm ? 10 : 12,
          gap: 8,
          ...(focused ? focusRing : {}),
        },
        style,
      ]}
    >
      {leftIcon && <Icon name={leftIcon} size={15} color={C.ink4} />}
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={C.ink4}
        keyboardType={numeric ? 'numeric' : 'default'}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onSubmitEditing={onSubmit}
        style={[
          {
            flex: 1,
            height: '100%',
            color: C.ink,
            fontSize: FS.md,
            fontFamily: mono ? FF.mono : FF.sans,
            fontWeight: FW.regular,
            // remove web outline (we render our own focus ring)
            ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : {}),
          },
        ]}
      />
    </View>
  );
};

export default TextField;

/**
 * TokenNumber — the inline `.bld-tok-num` % field in the rule sentence.
 * Accent-soft pill with a dashed accent underline.
 */
export const TokenNumber: React.FC<{ value: string; onChangeText: (v: string) => void; width?: number }> = ({
  value,
  onChangeText,
  width = 52,
}) => (
  <TextInput
    value={value}
    onChangeText={onChangeText}
    keyboardType="numeric"
    style={{
      width,
      paddingVertical: 3,
      paddingHorizontal: 6,
      backgroundColor: C.accentSoft,
      color: C.accentInk,
      borderBottomWidth: 2,
      borderBottomColor: C.accentLine,
      borderRadius: 6,
      textAlign: 'center',
      fontSize: 15,
      fontWeight: FW.semibold,
      fontFamily: FF.sans,
      ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : {}),
    }}
  />
);
